/**
 * Relative date labels for upcoming collections.
 *
 * Extracted from HomePage so the day-boundary arithmetic is testable: this is
 * the text that tells someone whether to put the bin out tonight, and an
 * off-by-one here is the difference between a correct app and a missed
 * collection.
 */

export type RelativeLabel =
    | { kind: 'today' }
    | { kind: 'tomorrow' }
    | { kind: 'weekday' }
    | { kind: 'in_days'; days: number }
    | { kind: 'past'; days: number };

/**
 * Classify a collection date relative to `now`.
 *
 * Comparison is done on calendar days in local time, so a collection at any
 * hour today reads as "today" regardless of the current time.
 */
export function getRelativeLabel(date: Date, now: Date = new Date()): RelativeLabel {
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    const startOfEvent = new Date(date);
    startOfEvent.setHours(0, 0, 0, 0);

    const days = Math.round(
        (startOfEvent.getTime() - startOfToday.getTime()) / 86_400_000
    );

    if (days < 0) return { kind: 'past', days: Math.abs(days) };
    if (days === 0) return { kind: 'today' };
    if (days === 1) return { kind: 'tomorrow' };
    if (days <= 7) return { kind: 'weekday' };
    return { kind: 'in_days', days };
}

/**
 * Whether a collection is still actionable.
 *
 * Collections happen in the morning, so a same-day event stops being useful
 * once the trucks have been round. Future days are always upcoming.
 */
export function isUpcoming(date: Date, now: Date = new Date(), cutoffHour = 11): boolean {
    const label = getRelativeLabel(date, now);

    if (label.kind === 'past') return false;
    if (label.kind === 'today') return now.getHours() < cutoffHour;
    return true;
}
